import React from 'react';
import AleaTable from '../../components/AleaTable/AleaTable';
import { ITableColumn } from '../../interfaces';
import { IResident } from '../../interfaces/IResident.ts';
import { IResidentListItem } from '../../interfaces/Resident/IResidentListItem.ts';
import { Flex, Text, useTheme } from '@chakra-ui/react';
import { StatusColors } from '../../constants/statusColors';
import StatusBox from '../../components/StatusBox/StatusBox.tsx';
import { useTranslation } from 'react-i18next';
import FeatureIcon from '../../components/FeatureIcon/FeatureIcon.tsx';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext.tsx';
import { UserRole } from '../../constants/UserRoles.ts';
import { ResidentFeature } from '../../constants/ResidentFeature.ts';
import AleaCheckIcon from '../../icons/AleaCheckIcon.tsx';

interface ResidentsTableProps {
  residents: IResidentListItem[];
  isLoading: boolean;
  stations: { id: string; name: string }[];
  onLoadMore?: () => void;
  total: number;
  searchQuery: string;
  setSearchQuery: React.Dispatch<React.SetStateAction<string>>;
  isTableListFetched: boolean;
  selectedFilter: string;
  careHomeFeatures?: string[];
  // Pagination props
  usePagination?: boolean;
  currentPage?: number;
  perPage?: number;
  onPageChange?: (page: number) => void;
  onPerPageChange?: (perPage: number) => void;
}

const ResidentsTable: React.FC<ResidentsTableProps> = ({
  residents,
  isLoading,
  total,
  selectedFilter,
  careHomeFeatures = [],
  // Pagination props
  usePagination = false,
  currentPage = 1,
  perPage = 10,
  onPageChange,
  onPerPageChange
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const theme = useTheme();

  const mappedResidents: IResident[] = residents.map((item) => ({
    id: item.id,
    rstate: item.state,
    last_name: item.last_name,
    first_name: item.first_name,
    room: item.room,
    has_alea_set: item.has_alea_set ?? false,
    carehome: item.carehome,
    station: {
      id: item.station?.id,
      name: item.station?.name || '',
    },
    features: Array.isArray(item.features)
      ? item.features.map((feature) => ({
          title: feature,
          label: feature,
          active: true,
        }))
      : [],
  }));

  const getColumns = (): ITableColumn<IResident>[] => {
    const baseColumns: ITableColumn<IResident>[] =
      selectedFilter === 'archived'
        ? []
        : [
            {
              key: 'rstate',
              label: t('status'),
              sortable: false,
              width: '76px',
              render: (data) => {
                return (
                  <StatusBox
                    status={data.rstate as keyof typeof StatusColors}
                  />
                );
              },
            },
          ];

    const featuresColumn = {
      key: 'features',
      label: t('features'),
      sortable: false,
      render: (data: IResident) => {
        const residentFeatures = new Set(
          data.features.map((f: { title: string }) => f.title)
        );

        const careHomeFeaturesSet = new Set(careHomeFeatures);

        return (
          <Flex alignItems="center" gap="1">
            {Object.values(ResidentFeature)
              .filter(
                (feature) =>
                  feature === ResidentFeature.INCONTINENCE_MANAGEMENT_STOOL
                    ? careHomeFeaturesSet.has(feature)
                    : true
              )
              .map((feature) => (
                <FeatureIcon
                  key={feature}
                  title={feature}
                  active={residentFeatures.has(feature)}
                  customColor={
                    residentFeatures.has(feature) ? undefined : '#C6C6C6'
                  }
                  alignment="flex-start"
                />
              ))}
          </Flex>
        );
      },
    };

    const aleaSetColumn = {
      key: 'has_alea_set',
      label: t('aleaSet'),
      sortable: true,
      render: (data: IResident) => (
        data.has_alea_set ? (
          <AleaCheckIcon color={theme.colors.alea[500]} size="15" />
        ) : (
          <Text color="#C6C6C6">––</Text>
        )
      ),
    };

    if (user?.role === UserRole.ORGANISATION_MANAGER) {
      return [
        ...baseColumns,
        {
          key: 'last_name',
          label: t('resident'),
          sortable: true,
          render: (data) => {
            return (
              <Text>{`${data.first_name} ${data.last_name} - ${data.room}`}</Text>
            );
          },
        },
        {
          key: 'station__name',
          label: t('livingArea'),
          sortable: true,
          render: (data) => {
            return <Text>{data.station?.name || '-'}</Text>;
          },
        },
        {
          key: 'carehome__name',
          label: t('careHome'),
          sortable: true,
          render: (data) => {
            return <Text>{data.carehome?.name || '-'}</Text>;
          },
        },
        aleaSetColumn,
        featuresColumn,
      ];
    }

    return [
      ...baseColumns,
      { key: 'last_name', label: t('lastName'), sortable: true },
      { key: 'first_name', label: t('firstName'), sortable: true },
      { key: 'room', label: t('room'), sortable: true },
      {
        key: 'station__name',
        label: t('livingArea'),
        sortable: true,
        render: (data) => {
          return <Text>{data.station?.name || '-'}</Text>;
        },
      },
      aleaSetColumn,
      featuresColumn,
    ];
  };

  const handleRowClick = (resident: IResident) => {
    navigate(`/residents/${resident.id}`);
  };

  return (
    <AleaTable<IResident>
      data={mappedResidents}
      columns={getColumns()}
      onRowClick={handleRowClick}
      isLoading={isLoading}
      total={total}
      noRecordsText={t('resident.noResidents')}
      usePagination={usePagination}
      currentPage={currentPage}
      perPage={perPage}
      onPageChange={onPageChange}
      onPerPageChange={onPerPageChange}
    />
  );
};

export default ResidentsTable;
